import { pxToRem } from '@/shared/styles/themes'
import { Button as BaseButton } from '@mui/material'
import { darken, lighten, styled } from '@mui/material/styles'

export const sizeMap = {
  small: {
    height: 30,
    padding: `${pxToRem(4)} ${pxToRem(12)}`,
    borderRadius: 4,
    fontSize: 13,
    iconMargin: 6,
    iconSize: pxToRem(16),
  },
  medium: {
    height: 36,
    padding: `${pxToRem(8)} ${pxToRem(12)}`,
    borderRadius: 4,
    fontSize: 14,
    iconMargin: 8,
    iconSize: pxToRem(16),
  },
  large: {
    height: 48,
    padding: `${pxToRem(12)} ${pxToRem(16)}`,
    borderRadius: 8,
    fontSize: 16,
    iconMargin: 10,
    iconSize: pxToRem(20),
  },
} as const

export type ButtonSize = keyof typeof sizeMap

export const StyledButton = styled(BaseButton, {
  shouldForwardProp: (prop) => prop !== '$round' && prop !== 'size',
})<{ $round?: boolean; size?: ButtonSize }>`
  font-size: ${props => pxToRem(sizeMap[props.size || 'medium'].fontSize)};
  font-weight: 400;
  text-transform: capitalize;
  letter-spacing: 0.04rem;
  border-radius: ${props => props.$round ? '1000px' : pxToRem(sizeMap[props.size || 'medium'].borderRadius)};
  padding: ${props => sizeMap[props.size || 'medium'].padding};
  height: ${props => pxToRem(sizeMap[props.size || 'medium'].height)} !important;

  &.MuiButton-containedPrimary {
    color: ${props => props.theme.palette.custom.button.primaryText};
    background-color: ${props => props.theme.palette.custom.button.primaryBackground};

    &:hover {
      color: ${({ theme }) => theme.palette.custom.button.primaryText};
      background-color: ${({ theme }) => theme.palette.mode === 'dark' ? darken(theme.palette.custom.button.primaryBackground, 0.2) : lighten(theme.palette.custom.button.primaryBackground, 0.2)};
    }

    &:active {
      color: ${({ theme }) => theme.palette.custom.button.primaryText};
      background-color: ${({ theme }) => theme.palette.mode === 'dark' ? darken(theme.palette.custom.button.primaryBackground, 0.3) : lighten(theme.palette.custom.button.primaryBackground, 0.3)};
    }
  }

  &.MuiButton-containedSecondary {
    &:hover {
      background-color: ${({ theme }) => theme.palette.mode === 'dark' ? darken(theme.palette.secondary.main, 0.2) : lighten(theme.palette.secondary.main, 0.2)};
    }

    &:active {
      background-color: ${({ theme }) => theme.palette.mode === 'dark' ? darken(theme.palette.secondary.main, 0.3) : lighten(theme.palette.secondary.main, 0.3)};
    }
  }

  .MuiButton-startIcon {
    margin-right: ${props => pxToRem(sizeMap[props.size || 'medium'].iconMargin)};
    margin-bottom: ${pxToRem(2)};
    
    svg {
      font-size: ${props => sizeMap[props.size || 'medium'].iconSize};
    }
  }

  .MuiButton-endIcon {
    svg {
      font-size: ${props => sizeMap[props.size || 'medium'].iconSize};
    }
  }

  ${props => props.variant === 'text' && `
    font-weight: 500;
    color: ${props.theme.palette.text.primary};
    background-color: unset;

    &:hover {
      color: ${props.theme.palette.text.primary};
      background-color: unset;
    }

    &:active {
      color: ${props.theme.palette.text.primary};
      background-color: unset;
    }

    &:after {
      content: '';
      position: absolute;
      top: 0;
      bottom: 0;
      right: 0;
      left: 0;
      width: 100%;
      height: 100%;
      border-radius: ${props.$round ? '1000px' : pxToRem(sizeMap[props.size || 'medium'].borderRadius)};
      background-color: ${props.theme.palette.action.hover};
      opacity: 0;
      transform: scaleX(1) scaleY(1) perspective(1px);
      transition: transform 0.1s cubic-bezier(0.35, 0.12, 0.14, 1.4), opacity 0.1s ease;
    }

    &:hover {
      &:after {
        opacity: 1;
        background-color: ${props.theme.palette.action.hover};
        transform: scaleX(1) scaleY(1) perspective(1px);
      }
    }

    &:active{
      &:after {
        transform: scaleX(0.95) scaleY(0.95) perspective(1px);
      }
    }
  `}

  ${props => props.variant === 'outlined' && `
    color: ${props.theme.palette.text.primary};
    background-color: unset;

    &:hover {
      color: ${props.theme.palette.text.primary};
      background-color: ${props.theme.palette.action.hover};
    }
  `}
  
  &:disabled {
    opacity: 0.6;
    color: ${props => props.theme.palette.text.disabled};
  }
`